import { useMemo, useState } from 'react';
import { HelpCircle, X, Search, MessageCircle, ChevronDown } from 'lucide-react';
import helpContent from '@/data/help.json';

const waLink = (texto) =>
  `https://wa.me/${helpContent.whatsapp}?text=${encodeURIComponent(texto || 'Olá! Preciso de ajuda com o Falo Com O Dono.')}`;

const flatten = () => {
  const out = [];
  Object.entries(helpContent.ambientes).forEach(([ambId, amb]) => {
    Object.entries(amb.telas).forEach(([telaId, tela]) => {
      tela.faq.forEach((item, i) => {
        out.push({ id: `${ambId}-${telaId}-${i}`, ambiente: amb.label, tela: tela.titulo, ...item });
      });
    });
  });
  return out;
};

const Item = ({ item, contexto }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        data-testid={`help-question-${item.id}`}
      >
        <span className="text-sm font-semibold text-[#0A2647]">{item.p}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 space-y-1">
          {contexto && <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{contexto}</p>}
          <p className="text-sm text-slate-600 leading-relaxed">{item.r}</p>
        </div>
      )}
    </div>
  );
};

export const HelpButton = ({ ambiente = 'dono', tela = 'dashboard' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const atual = helpContent.ambientes[ambiente]?.telas?.[tela]
    || helpContent.ambientes[ambiente]?.telas?.[Object.keys(helpContent.ambientes[ambiente]?.telas || {})[0]];

  const todos = useMemo(flatten, []);
  const busca = query.trim().toLowerCase();
  const resultados = busca.length >= 2
    ? todos.filter(i => `${i.p} ${i.r} ${i.tela}`.toLowerCase().includes(busca))
    : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[60] w-12 h-12 rounded-full bg-[#C5A059] text-[#0A2647] shadow-lg hover:scale-105 hover:bg-[#d4af61] transition-transform flex items-center justify-center font-bold"
        title="Ajuda desta tela"
        aria-label="Ajuda"
        data-testid="help-fab"
      >
        <HelpCircle className="w-6 h-6" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="help-panel">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-full sm:w-[420px] h-full bg-slate-50 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <header className="bg-[#0A2647] text-white px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#C5A059] font-bold">Ajuda</p>
                  <h2 className="text-lg font-bold" data-testid="help-screen-title">{atual?.titulo}</h2>
                </div>
                <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="help-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {atual?.dica && <p className="text-sm text-white/80 mt-2 leading-relaxed" data-testid="help-screen-tip">{atual.dica}</p>}
            </header>

            <div className="px-5 py-3 bg-white border-b border-slate-200">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar ajuda por palavra..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C5A059]/40"
                  data-testid="help-search-input"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="help-list">
              {resultados ? (
                resultados.length ? (
                  <>
                    <p className="text-xs text-slate-500 mb-1">{resultados.length} resultado(s) para "{query}"</p>
                    {resultados.map(i => <Item key={i.id} item={i} contexto={`${i.ambiente} · ${i.tela}`} />)}
                  </>
                ) : (
                  <div className="text-center py-10" data-testid="help-no-results">
                    <p className="text-sm text-slate-500">Não achamos nada com essa palavra.</p>
                    <p className="text-xs text-slate-400 mt-1">Chame a gente no WhatsApp que a gente resolve.</p>
                  </div>
                )
              ) : (
                (atual?.faq || []).map((item, i) => <Item key={i} item={{ ...item, id: `${ambiente}-${tela}-${i}` }} />)
              )}
            </div>

            <footer className="p-4 bg-white border-t border-slate-200">
              <a
                href={waLink(`Olá! Preciso de ajuda na tela "${atual?.titulo}" do Falo Com O Dono.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-colors"
                data-testid="help-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                {helpContent.whatsapp_label}
              </a>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
};

export default HelpButton;
